import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/form/Button";
import ActionBar from "../../../../components/elements/ActionBar";
import PromoterDrawer from "../../../../components/sections/front/promoter/PromoterDrawer";
import {
  invitation,
  organizationTeamList,
} from "../../../../services/organizationService";
import { useSelector } from "react-redux";
import { useOutletContext, useParams } from "react-router-dom";
import { useClipboard } from "../../../../util/Flag";
import Tooltip from "../../../../components/elements/Tooltip";
import PromoterDetailsDrawer from "../../../../components/sections/admin/Promoter/Drawer/PromoterDetailsDrawer";
import { t } from "i18next";

// Beautiful Status Badge Component - Enhanced Version
const StatusBadge = ({ status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      active: {
        label: "Active",
        bgColor: "bg-emerald-50",
        textColor: "text-emerald-700",
        borderColor: "border-emerald-400",
      },
      invited: {
        label: "Invited",
        bgColor: "bg-blue-50",
        textColor: "text-blue-700",
        borderColor: "border-blue-400",
      },
      suspended: {
        label: "Suspended",
        bgColor: "bg-red-50",
        textColor: "text-red-700",
        borderColor: "border-red-400",
      },
      pending: {
        label: "Pending",
        bgColor: "bg-amber-50",
        textColor: "text-amber-700",
        borderColor: "border-amber-400",
      },
      accepted: {
        label: "Accepted",
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        borderColor: "border-green-400",
      },
    };

    return (
      configs[status?.toLowerCase()] || {
        label: status || "Unknown",
        bgColor: "bg-gray-50",
        textColor: "text-gray-700",
        borderColor: "border-gray-400",
      }
    );
  };

  const config = getStatusConfig(status);

  return (
    <div
      className={`inline-flex items-center gap-2.5 px-3 py-1 rounded-full border transition-all duration-300 hover:shadow-lg ${config.bgColor} ${config.borderColor} ${config.glowColor}`}
    >
      <span
        className={`text-xs font-medium ${config.textColor} capitalize tracking-wide`}
      >
        {config.label}
      </span>
    </div>
  );
};

const Promoters = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization, organizations, selectedOrganizationDetails } =
    useSelector((state) => state?.organization);
  const { event, eventLoading, selectedLanguage } = useOutletContext();
  const { session } = useSelector((state) => state?.authProvider);
  const [selectedItems, setSelectedItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState({ isOpen: false, data: null });
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState({
    isOpen: false,
    data: null,
  });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const { copied, copy } = useClipboard();

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: teamData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["team-list-dashboard", filters, search, event?.data?._id],
    enabled: !!event?.data?._id,
    queryFn: async () => {
      const res = await organizationTeamList({
        ...filters,
        ...(search && { keyword: search }),
        organization: selectedOrganization,
        type: "promoter-invitation",
        event: event?.data?._id,
      });
      return res?.docs;
    },
  });

  const tableHeadData = [
    {
      _id: "Name",
      name: t("Name"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "referral_code",
      name: t("referral code"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "Email",
      name: t("Email"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "status",
      name: t("status"),
      align: "left",
      isSort: false,
      isFilter: false,
    },
    {
      _id: "actions",
      name: t("Action"),
      align: "right",
      isSort: false,
      isFilter: false,
    },
  ];

  const tableData = teamData?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.info?.displayName || "-",
        },
        {
          _id: "code",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.uid ? (
            <Tooltip
              messageClass={"!px-3 !py-2 !text-xm"}
              message={copied ? "Copied!" : "Copy"}
            >
              <button
                type="button"
                onClick={() => copy(item?.uid)}
                className="rounded-md py-2 px-2 h-9 text-center bg-primary/10 border whitespace-nowrap border-primary text-xs font-medium flex-1"
              >
                {item?.uid}
              </button>
            </Tooltip>
          ) : (
            "-"
          ),
        },
        {
          _id: "email",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.userData?.email || "-",
        },
        {
          _id: "status",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] !max-w-none !w-[10%]",
          data: item?.status ? <StatusBadge status={item?.status} /> : "-",
        },
        selectedOrganizationDetails?.data?.userData?._id ===
          session?.user?._id && {
          _id: "actions",
          type: "action",
          align: "right",
          cellClass: "!w-[1%]",
          actionData: [
            {
              _id: "details",
              name: t("Details"),
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                setDetailsDrawerOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: "edit",
              name: t("Edit"),
              icon: "fa-regular fa-pen-to-square",
              onClick: () => {
                setDrawerOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            {
              _id: "url",
              name: t("Copy URL"),
              icon: "fa-regular fa-copy",
              onClick: () => {
                const data = `${window.location?.origin}/${selectedLanguage}/event-details/${event?.data?.uid}?referral_code=${item?.uid}`;
                copy(data);
                toast.success("copied");
              },
              standout: false,
            },
            item?.status === "invited" && {
              _id: "invite",
              name: t("invite"),
              icon: "fa-regular fa-envelope",
              onClick: () => {
                invitation({
                  emails: [item?.userData?.email],
                  organization: event?.data?.organizationData?._id,
                  event: event?.data?._id,
                  invitationType: "promoter-invitation",
                }).then((res) => {
                  if (res?.status === 200) {
                    toast.success(res?.message);
                  }
                });
              },
              standout: false,
            },
          ]?.filter(Boolean),
        },
      ]?.filter(Boolean),
    };
  });

  // Order delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      // const res = await orderDelete({
      //   ids: Array.isArray(payload) ? payload : [payload],
      // });
      // return {
      //   res,
      //   payload: Array.isArray(payload) ? payload : [payload],
      // };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  function getResponseHandler({ res, type }) {
    if (type === "create") {
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["team-list-dashboard", filters, search, event?.data?._id],
        });
        setDrawerOpen({ isOpen: false, data: null });
      } else {
        toast.error(res?.error || res?.message);
      }
      return;
    }
  }

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex flex-wrap gap-2 pb-2 md:gap-0 justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">
              {t("Promoters")}
            </div>
            <div className="text-sm xl:text-base">
              {t(
                "Easily handle all promoter's actions: update promoter's info, status, and add new promoter's data"
              )}
              .
            </div>
          </div>
          <div className="relative mt-5 flex justify-end">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              buttonLabel={t("Create Promoter")}
              buttonLabelClasses={
                "font-medium !text-sm uppercase whitespace-nowrap"
              }
              buttonFunction={() => setDrawerOpen({ isOpen: true, data: null })}
            />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={teamData?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-full md:w-72">
              <Search
                placeholder={t("Search here")}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} {t("Selected")}
                  <button
                    onClick={() => setSelectedItems([])}
                    type="button"
                    className="cursor-pointer"
                  >
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={teamData?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(teamData?.data || [])]}
          setOriginalData={(val) => {}}
          handleOrdering={(newData, oldData) => {}}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>

      {/* Create/Edit Promoter Drawer */}
      <PromoterDrawer
        open={drawerOpen?.isOpen}
        data={drawerOpen?.data}
        close={() => {
          setDrawerOpen({ isOpen: false, data: null });
        }}
        getResponse={getResponseHandler}
      />

      {/* Promoter Details Drawer */}
      <PromoterDetailsDrawer
        open={detailsDrawerOpen?.isOpen}
        data={detailsDrawerOpen?.data}
        close={() => {
          setDetailsDrawerOpen({ isOpen: false, data: null });
        }}
      />

      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Promoters;
